import React, { useMemo, useState } from "react";
import AdminLayout from "../admin/AdminDashboardLayout";
import { motion } from "framer-motion";
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Tooltip } from '@mui/material';
import { usePartnerAnalytics } from "../../hooks/usePartnerAnalytics";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { PartnerResponse } from "../../types/Settings";
import { ApiRequestOptions } from "../../utils/api";
import Button from "../../utils/Button";
import toast from "react-hot-toast";
import HeatMap from "../../components/ui/HeatMap";
import { useDashboardRole } from "../../hooks/useDashboardRole";

interface PartnerMember {
  id: number;
  fullName: string;
  email: string;
  phone: string | null;
  createdAt: string;
  lastLoginAt?: string;
  lastLoginLocation?: string;
  enabled: boolean;
  onShift: boolean;
  totalRegistrations: number;
  role?: string;
}

interface MembersResponse {
  success: boolean;
  message: string;
  data: {
    content: PartnerMember[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}


export default function PartnerDashboard() {
  const { dashboardRole, isSupervisor } = useDashboardRole();
  const [showRedirectOptInModal, setShowRedirectOptInModal] = useState(false);
  const [hasRedirectOverageOptedIn, setHasRedirectOverageOptedIn] = useState(false);
  const [showReminderOptInModal, setShowReminderOptInModal] = useState(false);
  const [hasReminderOverageOptedIn, setHasReminderOverageOptedIn] = useState(false);
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = usePartnerAnalytics();

  const { data: membersData, isLoading: membersLoading } = useApiQuery<MembersResponse>({
    queryKey: ["partner-members"],
    request: {
      method: "GET",
      url: "/partner/members",
    },
  });

  const partnerQuery = useApiQuery<PartnerResponse>({
    queryKey: ["partner", "status"],
    request: {
      method: "GET",
      url: "/partner"
    } as ApiRequestOptions,
  });

  const allMembers = membersData?.data.content || [];
  const members = isSupervisor
    ? allMembers.filter((member) => member.role?.toUpperCase() === "CANVASSER")
    : allMembers;
  const activeShiftsCount = members.filter(member => member.onShift).length;

  const analytics = analyticsData?.data?.analytics || {
    totalLeads: 0,
    registeredLeads: 0,
    unregisteredLeads: 0,
    remindersSet: 0,
  };

  const currentPlan = partnerQuery.data?.data?.plan?.toLowerCase() || 'free';
  const isFreePlan = currentPlan === 'free';
  const redirectChecksUsed = partnerQuery.data?.data?.usage?.redirectChecksUsed || 0;
  const redirectChecksQuota = partnerQuery.data?.data?.usage?.redirectChecksQuota || 0;
  const reminderChecksUsed = partnerQuery.data?.data?.usage?.reminderOptInsUsed || 0;
  const reminderChecksQuota = partnerQuery.data?.data?.usage?.reminderOptInsQuota || 0;

  const redirectUsage = useMemo(() => {
    const ratio = redirectChecksQuota > 0
      ? redirectChecksUsed / redirectChecksQuota
      : redirectChecksUsed > 0
        ? 1
        : 0;

    return {
      ratio,
      percent: Math.round(ratio * 100),
      isNearLimit: ratio >= 0.9 && ratio < 1,
      isExhausted: ratio >= 1,
    };
  }, [redirectChecksQuota, redirectChecksUsed]);

  const reminderUsage = useMemo(() => {
    const ratio = reminderChecksQuota > 0
      ? reminderChecksUsed / reminderChecksQuota
      : reminderChecksUsed > 0
        ? 1
        : 0;

    return {
      ratio,
      percent: Math.round(ratio * 100),
      isNearLimit: ratio >= 0.9 && ratio < 1,
      isExhausted: ratio >= 1,
    };
  }, [reminderChecksQuota, reminderChecksUsed]);

  const redirectOptInMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Redirect overage billing enabled");
      setHasRedirectOverageOptedIn(true);
      setShowRedirectOptInModal(false);
      partnerQuery.refetch();
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to enable redirect overage billing";
      toast.error(errorMessage);
    }
  });

  const handleConfirmRedirectOptIn = () => {
    redirectOptInMutation.mutate({
      method: "PUT",
      url: "/partner/billing/overage-tracking",
      data: { enabled: true },
    });
  };

  const reminderOptInMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Reminder overage billing enabled");
      setHasReminderOverageOptedIn(true);
      setShowReminderOptInModal(false);
      partnerQuery.refetch();
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to enable reminder overage billing";
      toast.error(errorMessage);
    }
  });

  const handleConfirmReminderOptIn = () => {
    reminderOptInMutation.mutate({
      method: "PUT",
      url: "/partner/billing/overage-tracking",
      data: { enabled: true },
    });
  };

  const metrics = [
    {
      title: "Total Leads",
      value: analyticsLoading ? "..." : analytics.totalLeads.toLocaleString(),
      change: "+12%",
      trend: "up" as const,
      icon: <ChecklistRtlIcon />
    },
    {
      title: "Registered Leads",
      value: analyticsLoading ? "..." : analytics.registeredLeads.toLocaleString(),
      change: "+4.5%",
      trend: "up" as const,
      icon: <HowToRegIcon />
    },
    {
      title: "Active Shifts",
      value: activeShiftsCount.toString(),
      change: "+20%",
      trend: "up" as const,
      icon: <GpsNotFixedIcon />,
      disabled: isFreePlan

    },
    {
      title: "Redirect Checks",
      value: redirectChecksUsed,
      change: redirectChecksQuota ? `${redirectUsage.percent}%` : "+20%",
      trend: "up" as const,
      icon: <GpsNotFixedIcon />,
      total: redirectChecksQuota || 0,
      duration: partnerQuery.data?.data?.usage?.redirectChecksPeriod || "monthly"
    },
    {
      title: "Reminder Signups",
      value: partnerQuery.data?.data?.usage?.reminderOptInsUsed || 0,
      change: reminderChecksQuota ? `${reminderUsage.percent}%` : "",
      trend: "up" as const,
      icon: <MarkUnreadChatAltIcon />,
      total: reminderChecksQuota || 0,
      duration: partnerQuery.data?.data?.usage?.reminderOptInsPeriod || "yearly",
      disabled: isFreePlan
    }
  ];

  const districtProgress = [
    { name: "District 9 (Central)", progress: 45 },
    { name: "District 4 (North)", progress: 32 },
    { name: "District 12 (West)", progress: 23 }
  ];

  return (
    <AdminLayout role={dashboardRole}>
      <div className=" bg-gray-50 bg-slate-100 rounded-xl min-h-screen">


        <div className="bg-white bg-slate-300 rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex md:flex-row flex-col justify-between md:items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900  mb-2">
                {isSupervisor ? "Supervisor Overview" : "Partner Overview"}
              </h2>
              <div className="md:flex items-center gap-4">
                <div className=" hidden md:flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600 text-green-400 font-medium">Live Data</span>
                </div>
                {/* <span className="text-sm text-gray-500 text-gray-400">
                  Last updated: {format(new Date(), "MMM dd, yyyy 'at' h:mm a")}
                </span> */}
                {analyticsLoading && (
                  <span className="text-sm text-gray-500">Loading analytics...</span>
                )}
                {analyticsError && (
                  <span className="text-sm text-red-500">Failed to load analytics</span>
                )}
                {membersLoading ? (
                  <span className="text-sm text-gray-500">Loading shift status...</span>
                ) : activeShiftsCount > 0 ? (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">
                      {activeShiftsCount} active shift{activeShiftsCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-xs md:text-sm text-gray-500">No active shifts</span>
                  </div>
                )}
              </div>
            </div>
            {partnerQuery?.data?.data?.overageTrackingEnabled === true && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <p className="text-xs md:text-sm text-gray-500">Overage tracking is enabled</p>
              </div>
                )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6 mb-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
              <Tooltip
                title={metric.disabled ? "This feature is disabled for your current Free plan. Upgrade to enable reminder signups." : ""}
                placement="top"
                arrow
              >
                <div className={`relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] ${metric.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className="flex justify-between items-start mb-6">
                    <div className={`p-3 bg-gradient-to-br rounded-xl text-white shadow-lg ${metric.disabled ? 'from-gray-400 to-gray-500' : 'from-blue-500 to-purple-600'}`}>
                      {metric.icon}
                    </div>
                    {metric.change && !metric.disabled && (<div className={`px-3 py-1 rounded-full text-xs font-semibold ${parseInt(metric.change) >= 90 ? "bg-red-100 text-red-700 border-red-200/50" : "bg-green-100 text-green-700 border-green-200/50"} border `}>
                      {metric.change}
                    </div>)}
                  </div>
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                    {metric.total ? <span>{metric.value} <span className="text-gray-400 font-normal text-base">/ {metric.total}</span> </span> : metric.value}
                  </h3>
                  <p className="text-sm font-medium text-gray-600">
                    {!metric.total ? metric.title : `${metric.title} ${metric.duration}`}
                  </p>
                  <div className="mt-4 h-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: metric.total ? `${Math.min((metric.value / metric.total) * 100, 100)}%` : "75%" }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.8 }}
                      className={`h-full rounded-full ${metric.disabled ? 'bg-gradient-to-r from-gray-400 to-gray-500' : 'bg-gradient-to-r from-blue-500 to-purple-600'}`}
                    />
                  </div>
                </div>
              </Tooltip>
            </motion.div>
          ))}
        </div>

        {(redirectUsage.isNearLimit || redirectUsage.isExhausted) && partnerQuery?.data?.data?.overageTrackingEnabled === false && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`mb-8 rounded-2xl border p-5 shadow-sm ${redirectUsage.isExhausted
                ? "border-red-200 bg-red-50"
                : "border-amber-200 bg-amber-50"
              }`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${redirectUsage.isExhausted ? "text-red-600" : "text-amber-600"}`}>
                {redirectUsage.isExhausted ? (
                  <ErrorOutlineRoundedIcon />
                ) : (
                  <WarningAmberRoundedIcon />
                )}
              </div>

              <div className="flex-1">
                <h3 className={`text-base font-semibold ${redirectUsage.isExhausted ? "text-red-900" : "text-amber-900"}`}>
                  {redirectUsage.isExhausted
                    ? "Redirect quota exhausted"
                    : "Redirect checks almost exhausted"}
                </h3>

                <p className={`mt-1 text-sm leading-6 ${redirectUsage.isExhausted ? "text-red-800" : "text-amber-800"}`}>
                  {redirectUsage.isExhausted
                    ? "Your redirect checks quota has been exhausted. You and your canvassers can still run redirect checks, but new redirect activity, data, metrics, and analytics will no longer be tracked unless you opt in to overage billing at $0.0002 per redirect."
                    : `You have used ${redirectUsage.percent}% of your redirect checks for this plan. You are very close to exhausting your redirect checks quota.`}
                </p>

                {redirectUsage.isExhausted && (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {hasRedirectOverageOptedIn ? (
                      <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-100 px-4 py-2 text-sm font-medium text-green-800">
                        <CheckCircleRoundedIcon fontSize="small" />
                        Redirect overage billing is enabled
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        size="medium"
                        label="Opt-in"
                        additionalClassname="!w-auto px-5"
                        disabled={redirectOptInMutation.isPending}
                        onClick={() => setShowRedirectOptInModal(true)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {(reminderUsage.isNearLimit || reminderUsage.isExhausted) && partnerQuery?.data?.data?.overageTrackingEnabled === false && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`mb-8 rounded-2xl border p-5 shadow-sm ${reminderUsage.isExhausted
                ? "border-red-200 bg-red-50"
                : "border-amber-200 bg-amber-50"
              }`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 ${reminderUsage.isExhausted ? "text-red-600" : "text-amber-600"}`}>
                {reminderUsage.isExhausted ? (
                  <ErrorOutlineRoundedIcon />
                ) : (
                  <WarningAmberRoundedIcon />
                )}
              </div>

              <div className="flex-1">
                <h3 className={`text-base font-semibold ${reminderUsage.isExhausted ? "text-red-900" : "text-amber-900"}`}>
                  {reminderUsage.isExhausted
                    ? "Reminder quota exhausted"
                    : "Reminder quota almost exhausted"}
                </h3>

                <p className={`mt-1 text-sm leading-6 ${reminderUsage.isExhausted ? "text-red-800" : "text-amber-800"}`}>
                  {reminderUsage.isExhausted
                    ? "Your reminder quota has been exhausted. You and your canvassers can still send reminders, but new reminder activity, data, metrics, and analytics will no longer be tracked unless you opt in to overage billing at $0.0002 per reminder."
                    : `You have used ${reminderUsage.percent}% of your reminder quota for this plan. You are very close to exhausting your reminder quota.`}
                </p>

                {reminderUsage.isExhausted && (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {hasReminderOverageOptedIn ? (
                      <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-100 px-4 py-2 text-sm font-medium text-green-800">
                        <CheckCircleRoundedIcon fontSize="small" />
                        Reminder overage billing is enabled
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        size="medium"
                        label="Opt-in"
                        additionalClassname="!w-auto px-5"
                        disabled={reminderOptInMutation.isPending}
                        onClick={() => setShowReminderOptInModal(true)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}



        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white bg-slate-800 rounded-xl shadow-sm overflow-hidden"
        >
          <div className="shadow-md border rounded-lg border-neutral-300">
            <HeatMap />
          </div>
        </motion.div>

        {showRedirectOptInModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-white/60 backdrop-blur-md"
              onMouseDown={() => {
                if (!redirectOptInMutation.isPending) {
                  setShowRedirectOptInModal(false);
                }
              }}
            />

            <div
              className="relative z-10 w-[700px] max-w-[95%] overflow-hidden rounded-[32px] border border-white/80 bg-white/95 text-gray-900 shadow-[0_30px_120px_rgba(15,23,42,0.16)] backdrop-blur-xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-r from-rose-100 via-orange-50 to-amber-100" />
              <div className="absolute right-0 top-0 h-44 w-44 translate-x-10 -translate-y-6 rounded-full bg-rose-200/40 blur-3xl" />
              <div className="absolute left-8 top-16 h-24 w-24 rounded-full bg-amber-200/50 blur-2xl" />

              <div className="relative p-7 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="max-w-xl">
                    <div className="mb-4 inline-flex items-center rounded-full border border-rose-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-600 shadow-sm">
                      Redirect Overage
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 text-white shadow-[0_16px_36px_rgba(244,63,94,0.28)]">
                        <WarningAmberRoundedIcon />
                      </div>

                      <div>
                        <h2 className="text-2xl font-semibold tracking-tight text-gray-950">Enable redirect overage billing</h2>
                        <p className="mt-2 text-sm leading-7 text-gray-600">
                          You will be automatically billed every month for all redirects used within the next billing cycle alongside your bill.
                        </p>
                        <p className="mt-2 text-sm leading-7 text-gray-600">
                          The overage billing DOES NOT INCLUDE the base redirect allowance included in your current plan
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/80 bg-white/80 text-sm font-medium text-gray-500 shadow-sm transition-all hover:scale-105 hover:text-gray-800"
                    onClick={() => {
                      if (!redirectOptInMutation.isPending) {
                        setShowRedirectOptInModal(false);
                      }
                    }}
                  >
                    x
                  </button>
                </div>

                <div className="mt-7 grid gap-4 md:grid-cols-[1.35fr_0.85fr]">
                  <div className="rounded-[28px] border border-rose-100 bg-gradient-to-br from-white via-rose-50/60 to-orange-50/70 p-5 shadow-[0_12px_40px_rgba(251,113,133,0.10)]">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">Billing Summary</div>
                    <p className="mt-3 text-sm leading-7 text-gray-700">
                      Overage redirect checks will be billed at <span className="font-semibold text-gray-950">$0.002 per redirect</span>.
                    </p>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white bg-white/90 p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Rate</div>
                        <div className="mt-2 text-2xl font-semibold tracking-tight text-gray-950">$0.002</div>
                        <div className="mt-1 text-sm text-gray-500">per redirect</div>
                      </div>

                      <div className="rounded-2xl border border-white bg-white/90 p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Cycle</div>
                        <div className="mt-2 text-base font-semibold text-gray-950">Monthly billing</div>
                        <div className="mt-1 text-sm text-gray-500">rolled into your bill automatically</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-5">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">What You Keep</div>
                    <div className="mt-4 space-y-3">
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <div className="text-sm font-medium text-gray-900">Analytics tracking</div>
                        <div className="mt-1 text-sm leading-6 text-gray-500">Redirect data and dashboard metrics continue without interruption.</div>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <div className="text-sm font-medium text-gray-900">Automatic reconciliation</div>
                        <div className="mt-1 text-sm leading-6 text-gray-500">All usage inside the next billing cycle is added automatically.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-7 flex flex-col-reverse justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={redirectOptInMutation.isPending}
                    onClick={() => setShowRedirectOptInModal(false)}
                  >
                    Cancel
                  </button>

                  <Button
                    variant="primary"
                    size="medium"
                    label={redirectOptInMutation.isPending ? "Opting in..." : "Confirm opt-in"}
                    additionalClassname="!w-auto rounded-full !px-6  shadow-[0_16px_34px_rgba(244,63,94,0.28)] hover:opacity-95"
                    disabled={redirectOptInMutation.isPending}
                    onClick={handleConfirmRedirectOptIn}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {showReminderOptInModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-white/60 backdrop-blur-md"
              onMouseDown={() => {
                if (!reminderOptInMutation.isPending) {
                  setShowReminderOptInModal(false);
                }
              }}
            />

            <div
              className="relative z-10 w-[700px] max-w-[95%] overflow-hidden rounded-[32px] border border-white/80 bg-white/95 text-gray-900 shadow-[0_30px_120px_rgba(15,23,42,0.16)] backdrop-blur-xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-r from-rose-100 via-orange-50 to-amber-100" />
              <div className="absolute right-0 top-0 h-44 w-44 translate-x-10 -translate-y-6 rounded-full bg-rose-200/40 blur-3xl" />
              <div className="absolute left-8 top-16 h-24 w-24 rounded-full bg-amber-200/50 blur-2xl" />

              <div className="relative p-7 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="max-w-xl">
                    <div className="mb-4 inline-flex items-center rounded-full border border-rose-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-600 shadow-sm">
                      Reminder Overage
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 text-white shadow-[0_16px_36px_rgba(244,63,94,0.28)]">
                        <WarningAmberRoundedIcon />
                      </div>

                      <div>
                        <h2 className="text-2xl font-semibold tracking-tight text-gray-950">Enable reminder overage billing</h2>
                        <p className="mt-2 text-sm leading-7 text-gray-600">
                          You will be automatically billed every month for all reminders used within the next billing cycle alongside your bill.
                        </p>
                        <p className="mt-2 text-sm leading-7 text-gray-600">
                          The overage billing DOES NOT INCLUDE the base reminder allowance included in your current plan
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/80 bg-white/80 text-sm font-medium text-gray-500 shadow-sm transition-all hover:scale-105 hover:text-gray-800"
                    onClick={() => {
                      if (!reminderOptInMutation.isPending) {
                        setShowReminderOptInModal(false);
                      }
                    }}
                  >
                    x
                  </button>
                </div>

                <div className="mt-7 grid gap-4 md:grid-cols-[1.35fr_0.85fr]">
                  <div className="rounded-[28px] border border-rose-100 bg-gradient-to-br from-white via-rose-50/60 to-orange-50/70 p-5 shadow-[0_12px_40px_rgba(251,113,133,0.10)]">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">Billing Summary</div>
                    <p className="mt-3 text-sm leading-7 text-gray-700">
                      Overage reminders will be billed at <span className="font-semibold text-gray-950">$0.005 per reminder</span>.
                    </p>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white bg-white/90 p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Rate</div>
                        <div className="mt-2 text-2xl font-semibold tracking-tight text-gray-950">$0.005</div>
                        <div className="mt-1 text-sm text-gray-500">per reminder</div>
                      </div>

                      <div className="rounded-2xl border border-white bg-white/90 p-4 shadow-sm">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">Cycle</div>
                        <div className="mt-2 text-base font-semibold text-gray-950">Monthly billing</div>
                        <div className="mt-1 text-sm text-gray-500">rolled into your bill automatically</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-100 bg-slate-50/80 p-5">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">What You Keep</div>
                    <div className="mt-4 space-y-3">
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <div className="text-sm font-medium text-gray-900">Analytics tracking</div>
                        <div className="mt-1 text-sm leading-6 text-gray-500">Reminder data and dashboard metrics continue without interruption.</div>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <div className="text-sm font-medium text-gray-900">Automatic reconciliation</div>
                        <div className="mt-1 text-sm leading-6 text-gray-500">All usage inside the next billing cycle is added automatically.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-7 flex flex-col-reverse justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
                  <button
                    type="button"
                    className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={reminderOptInMutation.isPending}
                    onClick={() => setShowReminderOptInModal(false)}
                  >
                    Cancel
                  </button>

                  <Button
                    variant="primary"
                    size="medium"
                    label={reminderOptInMutation.isPending ? "Opting in..." : "Confirm opt-in"}
                    additionalClassname="!w-auto rounded-full !px-6  shadow-[0_16px_34px_rgba(244,63,94,0.28)] hover:opacity-95"
                    disabled={reminderOptInMutation.isPending}
                    onClick={handleConfirmReminderOptIn}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
